'use strict';

/**
 * Site navigation (snippets/site-nav.liquid). One panel with Products and Help/Info tabs:
 * - Desktop (>= 768 px): the menu button opens a dropdown panel. Product groups on the left, the selected group's
 *   columns on the right. Click, Enter or Space selects a group; hovering with a mouse selects after 120 ms.
 *   Up/Down move between groups, Right moves into the group's links, Left comes back. Esc closes and returns focus.
 * - Mobile: the hamburger opens the drawer with the group list; a group opens as a second page with a back button.
 * Disclosure pattern (aria-expanded), not ARIA menu roles. Closes on click outside or when focus leaves the header.
 */
(function () {
  if (window.PippeiSiteNavRebind) {
    window.PippeiSiteNavRebind();
    return;
  }
  var cleanup = null;
  function bind() {
  if (cleanup) cleanup();
  var nav = document.getElementById('site-nav');
  if (!nav) return;
  // The editor-owned menu is a sibling section of the header, not a child of it.
  var header = document.querySelector('.site-top');
  if (!header) return;
  var toggles = [].slice.call(document.querySelectorAll('[data-site-nav-toggle]'));
  var tabs = [].slice.call(nav.querySelectorAll('[data-nav-tab]'));
  var views = [].slice.call(nav.querySelectorAll('[data-nav-view]'));
  var groups = [].slice.call(nav.querySelectorAll('[data-nav-group]'));
  var panes = [].slice.call(nav.querySelectorAll('[data-nav-pane]'));
  var rail = [].slice.call(nav.querySelectorAll('.site-nav__group'));
  var mq = window.matchMedia('(min-width: 768px)');
  var hoverTimer = null;
  var controller = new AbortController();
  var signal = controller.signal;
  cleanup = function () {
    clearTimeout(hoverTimer);
    controller.abort();
    if (!mq.addEventListener && mq.removeListener) mq.removeListener(onChange);
  };

  function isOpen() { return !nav.hidden; }

  function placePanel() {
    document.documentElement.style.setProperty('--site-nav-top', Math.max(0, header.getBoundingClientRect().bottom) + 'px');
  }

  function setPane(id) {
    groups.forEach(function (b) { b.setAttribute('aria-expanded', String(b.getAttribute('data-nav-group') === String(id))); });
    panes.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-nav-pane') === String(id)); });
    if (id && !mq.matches) nav.setAttribute('data-pane-open', '');
    else nav.removeAttribute('data-pane-open');
  }

  function activeGroup() {
    return groups.filter(function (b) { return b.getAttribute('aria-expanded') === 'true'; })[0] || null;
  }

  function activeTab() {
    var tab = tabs.filter(function (b) { return b.getAttribute('aria-selected') === 'true'; })[0];
    return tab ? tab.getAttribute('data-nav-tab') : 'products';
  }

  function setTab(name) {
    tabs.forEach(function (tab) {
      var selected = tab.getAttribute('data-nav-tab') === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    views.forEach(function (view) { view.hidden = view.getAttribute('data-nav-view') !== name; });
    setPane(name === 'products' && mq.matches && groups[0] ? groups[0].getAttribute('data-nav-group') : null);
  }

  function setOpen(open) {
    if (open) placePanel();
    nav.hidden = !open;
    if (header.setAttribute) { if (open) header.setAttribute('data-nav-open', ''); else header.removeAttribute('data-nav-open'); }
    if (open) document.dispatchEvent(new CustomEvent('site-nav:open'));
    toggles.forEach(function (t) {
      t.setAttribute('aria-expanded', String(open));
      var o = t.getAttribute('data-label-open'), c = t.getAttribute('data-label-close');
      if (o && c) t.setAttribute('aria-label', open ? c : o);
    });
    if (open) setTab('products');
    else setPane(null);
  }

  function visibleToggle() {
    return toggles.filter(function (t) { return t.offsetParent !== null; })[0] || toggles[0];
  }

  function focusPane(id) {
    var link = nav.querySelector('[data-nav-pane="' + id + '"] a');
    if (link) link.focus();
  }

  toggles.forEach(function (t) {
    t.addEventListener('click', function () { setOpen(!isOpen()); }, { signal: signal });
  });

  tabs.forEach(function (tab, index) {
    tab.addEventListener('click', function () { setTab(tab.getAttribute('data-nav-tab')); }, { signal: signal });
    tab.addEventListener('keydown', function (e) {
      var target = e.key === 'ArrowRight' ? tabs[(index + 1) % tabs.length]
        : e.key === 'ArrowLeft' ? tabs[(index + tabs.length - 1) % tabs.length]
        : e.key === 'Home' ? tabs[0]
        : e.key === 'End' ? tabs[tabs.length - 1] : null;
      if (!target) return;
      e.preventDefault();
      setTab(target.getAttribute('data-nav-tab'));
      target.focus();
    }, { signal: signal });
  });

  groups.forEach(function (b) {
    var id = b.getAttribute('data-nav-group');
    b.addEventListener('click', function (e) {
      setPane(id);
      if (mq.matches && e.detail === 0) focusPane(id);
      else if (!mq.matches) {
        var back = nav.querySelector('[data-nav-pane="' + id + '"] [data-nav-back]');
        if (back) back.focus();
      }
    }, { signal: signal });
    b.addEventListener('mouseenter', function () {
      if (!mq.matches) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { setPane(id); }, 120);
    }, { signal: signal });
    b.addEventListener('mouseleave', function () { clearTimeout(hoverTimer); }, { signal: signal });
    b.addEventListener('keydown', function (e) {
      if (!mq.matches) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); setPane(id); focusPane(id); }
    }, { signal: signal });
  });

  rail.forEach(function (el, i) {
    el.addEventListener('keydown', function (e) {
      if (!mq.matches) return;
      var next = e.key === 'ArrowDown' ? rail[i + 1] : e.key === 'ArrowUp' ? rail[i - 1] : null;
      if (!next) return;
      e.preventDefault();
      next.focus();
      if (next.hasAttribute('data-nav-group')) setPane(next.getAttribute('data-nav-group'));
    }, { signal: signal });
  });

  panes.forEach(function (p) {
    var id = p.getAttribute('data-nav-pane');
    var back = p.querySelector('[data-nav-back]');
    function toGroup() {
      var b = nav.querySelector('[data-nav-group="' + id + '"]');
      if (!mq.matches) setPane(null);
      if (b) b.focus();
    }
    if (back) back.addEventListener('click', toGroup, { signal: signal });
    p.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft' && mq.matches) { e.preventDefault(); toGroup(); }
    }, { signal: signal });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !isOpen()) return;
    var b = activeGroup();
    if (!mq.matches && b) {
      setPane(null);
      b.focus();
      return;
    }
    setOpen(false);
    var t = visibleToggle();
    if (t) t.focus();
  }, { signal: signal });

  document.addEventListener('click', function (e) {
    if (isOpen() && !header.contains(e.target) && !nav.contains(e.target)) setOpen(false);
  }, { signal: signal });

  // Focusing the search field closes the panel (assets/search-suggest.js).
  document.addEventListener('site-nav:close', function () { if (isOpen()) setOpen(false); }, { signal: signal });

  // Keep the selected block visible while editing this separate menu section.
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target && e.target.contains(nav)) setOpen(true);
  }, { signal: signal });
  document.addEventListener('shopify:block:select', function (e) {
    if (!e.target || !nav.contains(e.target)) return;
    setOpen(true);
    setTab('products');
    var pane = e.target.closest('[data-nav-pane]');
    if (pane) setPane(pane.getAttribute('data-nav-pane'));
    else if (e.target.hasAttribute('data-nav-group')) setPane(e.target.getAttribute('data-nav-group'));
  }, { signal: signal });

  header.addEventListener('focusout', function (e) {
    if (isOpen() && mq.matches && e.relatedTarget && !header.contains(e.relatedTarget) && !nav.contains(e.relatedTarget)) setOpen(false);
  }, { signal: signal });
  nav.addEventListener('focusout', function (e) {
    if (isOpen() && mq.matches && e.relatedTarget && !header.contains(e.relatedTarget) && !nav.contains(e.relatedTarget)) setOpen(false);
  }, { signal: signal });

  window.addEventListener('scroll', function () { if (isOpen()) placePanel(); }, { passive: true, signal: signal });
  window.addEventListener('resize', function () { if (isOpen()) placePanel(); }, { signal: signal });

  var onChange = function () {
    if (!isOpen()) return;
    placePanel();
    var selected = activeTab();
    setTab(selected);
  };
  if (mq.addEventListener) mq.addEventListener('change', onChange, { signal: signal });
  else if (mq.addListener) mq.addListener(onChange);
  }
  window.PippeiSiteNavRebind = bind;
  document.addEventListener('shopify:section:load', bind);
  bind();
})();
